
import { createContext, useContext, useState, useEffect } from 'react';
import { getSettings } from '../api/settings.api';
import { HashLoader } from 'react-spinners';

const SettingsContext = createContext();
const SETTINGS_CACHE_KEY = "settings-context-cache-v1";
const SETTINGS_CACHE_TTL_MS = 10 * 60 * 1000;

const parseSettingsConfig = (config) => {
    if (!config) return {};
    if (typeof config === "object") return config;
    try {
        return JSON.parse(config);
    } catch (_err) {
        return {};
    }
};

const readSettingsCache = () => {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(SETTINGS_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
    } catch (_err) {
        return null;
    }
};

const writeSettingsCache = (payload) => {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(
            SETTINGS_CACHE_KEY,
            JSON.stringify({
                cachedAt: Date.now(),
                data: payload,
            })
        );
    } catch (_err) {
        // ignore storage errors
    }
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState({});
    const [termOfService, setTermOfService] = useState([]);
    const [privacyPolicy, setPrivacyPolicy] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let hasHydratedFromCache = false;

        const applySettingsPayload = (payload) => {
            setSettings(parseSettingsConfig(payload?.config));
            setTermOfService(Array.isArray(payload?.term_of_service) ? payload.term_of_service : []);
            setPrivacyPolicy(Array.isArray(payload?.privacy_policy) ? payload.privacy_policy : []);
        };

        const fetchSettings = async () => {
            try {
                const res = await getSettings();
                if (res.success) {
                    const payload = res?.data?.settings || {};
                    applySettingsPayload(payload);
                    writeSettingsCache(payload);
                }
            } catch (err) {
                console.error("Failed to load settings:", err);
            } finally {
                if (!hasHydratedFromCache) {
                    setLoading(false);
                }
            }
        };

        const cached = readSettingsCache();
        if (cached?.data) {
            applySettingsPayload(cached.data);
            hasHydratedFromCache = true;
            setLoading(false);
        }

        const isCacheFresh = cached?.cachedAt
            ? Date.now() - Number(cached.cachedAt) < SETTINGS_CACHE_TTL_MS
            : false;

        if (!isCacheFresh) {
            fetchSettings();
        }
    }, []);

    if (loading) {
        return (
            <div style={{
                width: "100%",
                height: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
            }}>
                <HashLoader color="var(--hash-loader-color)" />
            </div>
        );
    }

    return (
        <SettingsContext.Provider value={{settings, privacyPolicy, termOfService}}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => useContext(SettingsContext);
